import { LoadingOutlined } from '@ant-design/icons'
import { GraviButton, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Alert, Modal } from 'antd'
import React, { useState, useEffect } from 'react'
import { StatusMessageGrid } from './Grid'
import { UploadStatusCards } from './StatusCards'
import type { UploadStatusNormalized } from './utils/uploadStatus'

interface UploadStatusModalProps {
  uploadStatusModalVisible: boolean
  setUploadStatusModalVisible: (visible: boolean) => void
  statusResponse: UploadStatusNormalized | undefined
  isUploading: boolean
  onClose?: () => void
}

export function UploadStatusModal({
  uploadStatusModalVisible,
  setUploadStatusModalVisible,
  statusResponse,
  isUploading,
  onClose,
}: UploadStatusModalProps) {
  const [statusView, setStatusView] = useState<string>('')

  const handleClose = () => {
    setUploadStatusModalVisible(false)
    onClose?.()
  }

  useEffect(() => {
    if (statusResponse?.error_count > 0) {
      setStatusView('error')
    } else if (statusResponse?.warning_count > 0) {
      setStatusView('warning')
    } else if (
      statusResponse?.warning_count === 0 &&
      statusResponse?.error_count === 0
    ) {
      setStatusView('')
    }
  }, [statusResponse])

  return (
    <Modal
      closable={!isUploading}
      maskClosable={!isUploading}
      footer={
        !isUploading && (
          <GraviButton buttonText='Dismiss' onClick={handleClose} />
        )
      }
      title={<Texto>Upload Status</Texto>}
      open={uploadStatusModalVisible}
      onCancel={handleClose}
      width={1000}
    >
      <ModalContents
        statusResponse={statusResponse}
        statusView={statusView}
        setStatusView={setStatusView}
        loading={isUploading}
      />
    </Modal>
  )
}

interface ModalContentsProps {
  statusResponse: UploadStatusNormalized
  statusView: string
  setStatusView: (view: string) => void
  loading: boolean
}

function ModalContents({
  statusResponse,
  statusView,
  setStatusView,
  loading,
}: ModalContentsProps) {
  if (loading) {
    return (
      <Vertical
        alignItems='center'
        justifyContent='center'
        style={{ padding: '40px 0' }}
      >
        <LoadingOutlined
          style={{ fontSize: '64px', color: 'var(--theme-primary)' }}
        />
        <Vertical gap={16} alignItems='center' style={{ marginTop: '24px' }}>
          <Texto weight='bold'>Uploading Spreadsheet</Texto>
          <Texto appearance='hint'>
            This might take a minute. Please wait until the upload results are
            ready.
          </Texto>
        </Vertical>
      </Vertical>
    )
  }

  if (!statusResponse) return null

  return (
    <Vertical gap={16}>
      <StatusHeader statusResponse={statusResponse} />
      <UploadStatusCards
        statusResponse={statusResponse}
        statusView={statusView}
      />
      <StatusMessageGrid
        setStatusView={setStatusView}
        statusView={statusView}
        statusResponse={statusResponse}
      />
    </Vertical>
  )
}

interface StatusHeaderProps {
  statusResponse: UploadStatusNormalized
}

function StatusHeader({ statusResponse }: StatusHeaderProps) {
  const isError = statusResponse.errors.length > 0

  if (isError) {
    return (
      <Alert
        description={statusResponse.message}
        message='Upload Error'
        type='error'
        showIcon
      />
    )
  }

  return (
    <Alert
      type='success'
      message='Upload Complete'
      description={statusResponse.message}
      className='flex items-center'
      showIcon
    />
  )
}
